/**
 * S4-C2 — Helpers purs pour la politique de paiement par mode (admin).
 * Aucune sync serveur ; suggestion legacy uniquement sur action explicite.
 */

export const POLITIQUE_PAIEMENT_PROTECTED_FIELDS = Object.freeze([
  'carteAutoriseeLivraison',
  'surPlaceAutoriseLivraison',
  'carteAutoriseeRetrait',
  'surPlaceAutoriseRetrait',
  'politiquePaiementParModeActive',
]);

export const CODE_POLITIQUE_PAIEMENT_ACTIVATION_DESACTIVEE =
  'POLITIQUE_PAIEMENT_ACTIVATION_DESACTIVEE';
export const CODE_POLITIQUE_PAIEMENT_INVALIDE = 'POLITIQUE_PAIEMENT_INVALIDE';
export const CODE_POLITIQUE_PAIEMENT_INACTIVE = 'POLITIQUE_PAIEMENT_INACTIVE';
export const CODE_COMMERCANT_NOT_FOUND = 'COMMERCANT_NOT_FOUND';

/** Matrice vide — choix explicite requis (active=false). */
export function emptyMatriceDraft() {
  return {
    carteAutoriseeLivraison: false,
    surPlaceAutoriseLivraison: false,
    carteAutoriseeRetrait: false,
    surPlaceAutoriseRetrait: false,
  };
}

/**
 * Suggestion locale depuis les flags legacy globaux (miroir simple).
 * Ne lit PAS la matrice dormant en base.
 */
export function buildMatriceSuggestionFromLegacy(paiementEnLigne, paiementSurPlace) {
  const carte = paiementEnLigne === true;
  const surPlace = paiementSurPlace === true;
  return {
    carteAutoriseeLivraison: carte,
    carteAutoriseeRetrait: carte,
    surPlaceAutoriseLivraison: surPlace,
    surPlaceAutoriseRetrait: surPlace,
  };
}

/**
 * Draft initial quand active !== true :
 * - ignore la matrice stockée en base (backfill / défauts / ancienne activation) ;
 * - repart d’un choix explicite vide.
 */
export function buildInactiveMatriceDraft(_commercant) {
  return emptyMatriceDraft();
}

/**
 * Draft quand active === true : valeurs serveur de la matrice.
 */
export function buildActiveMatriceDraft(commercant) {
  return {
    carteAutoriseeLivraison: commercant?.carteAutoriseeLivraison === true,
    surPlaceAutoriseLivraison: commercant?.surPlaceAutoriseLivraison === true,
    carteAutoriseeRetrait: commercant?.carteAutoriseeRetrait === true,
    surPlaceAutoriseRetrait: commercant?.surPlaceAutoriseRetrait === true,
  };
}

export function isPolitiquePaiementParModeActive(commercant) {
  return commercant?.politiquePaiementParModeActive === true;
}

/**
 * Affichage Admin lecture seule quand la politique par mode n’est pas active.
 * Source de vérité = flags legacy globaux (pas la matrice dormante).
 * Aucune écriture. Générique pour tout commerçant.
 *
 * @param {object|null|undefined} commercant
 * @returns {{ paiementEnLigne: boolean, paiementSurPlace: boolean }}
 */
export function buildLegacyPaiementDisplay(commercant) {
  return {
    paiementEnLigne: commercant?.paiementEnLigne === true,
    paiementSurPlace: commercant?.paiementSurPlace === true,
  };
}

/** Draft Prestations & RDV — champs legacy boutique (canal prestation). */
export function buildPrestationPaiementDraft(commercant) {
  return buildLegacyPaiementDisplay(commercant);
}

/** Payload updateCommercant — uniquement paiementEnLigne / paiementSurPlace. */
export function buildPrestationPaiementApiPayload(draft) {
  return {
    paiementEnLigne: draft?.paiementEnLigne === true,
    paiementSurPlace: draft?.paiementSurPlace === true,
  };
}

export function isPrestationPaiementDraftDirty(draft, commercant) {
  const server = buildPrestationPaiementDraft(commercant);
  return (
    draft?.paiementEnLigne !== server.paiementEnLigne
    || draft?.paiementSurPlace !== server.paiementSurPlace
  );
}

/**
 * @param {boolean} enabled
 * @returns {'Activé'|'Désactivé'}
 */
export function formatLegacyActivationLabel(enabled) {
  return enabled === true ? 'Activé' : 'Désactivé';
}

export const LEGACY_POLICY_EXPLANATION =
  'Cette boutique utilise actuellement la configuration générale des moyens de paiement. La configuration par mode de commande n’est pas activée pour cette boutique.';

/** Matrice éditable uniquement si la politique par mode est active. */
export function shouldShowPerModeMatrix(commercant) {
  return isPolitiquePaiementParModeActive(commercant);
}

/**
 * Résout le draft affiché selon l’état serveur + mémoire de session.
 * Si active=false : ignore la matrice DB sauf si sessionKeptDraft (après désactivation locale).
 */
export function resolveMatriceDraft({ commercant, sessionKeptDraft = null }) {
  if (isPolitiquePaiementParModeActive(commercant)) {
    return {
      draft: buildActiveMatriceDraft(commercant),
      clearSessionKept: true,
    };
  }
  if (sessionKeptDraft && typeof sessionKeptDraft === 'object') {
    return {
      draft: {
        carteAutoriseeLivraison: sessionKeptDraft.carteAutoriseeLivraison === true,
        surPlaceAutoriseLivraison: sessionKeptDraft.surPlaceAutoriseLivraison === true,
        carteAutoriseeRetrait: sessionKeptDraft.carteAutoriseeRetrait === true,
        surPlaceAutoriseRetrait: sessionKeptDraft.surPlaceAutoriseRetrait === true,
      },
      clearSessionKept: false,
    };
  }
  return {
    draft: buildInactiveMatriceDraft(commercant),
    clearSessionKept: false,
  };
}

/**
 * Validation front : ≥1 moyen par mode actif.
 * Modes inactifs : pas d’obligation.
 * @returns {{ ok: true }|{ ok: false, modesActifsSansMoyen: string[] }}
 */
export function validateMatriceDraftForModes({ livraison, clickAndCollect, matrice }) {
  const modesActifsSansMoyen = [];
  if (livraison === true) {
    const ok =
      matrice?.carteAutoriseeLivraison === true
      || matrice?.surPlaceAutoriseLivraison === true;
    if (!ok) modesActifsSansMoyen.push('livraison');
  }
  if (clickAndCollect === true) {
    const ok =
      matrice?.carteAutoriseeRetrait === true
      || matrice?.surPlaceAutoriseRetrait === true;
    if (!ok) modesActifsSansMoyen.push('clickandcollect');
  }
  if (modesActifsSansMoyen.length > 0) {
    return { ok: false, modesActifsSansMoyen };
  }
  return { ok: true };
}

/** Payload activation / PUT matrice : exactement les 4 booléens. */
export function buildMatriceApiPayload(matrice) {
  return {
    carteAutoriseeLivraison: matrice.carteAutoriseeLivraison === true,
    surPlaceAutoriseLivraison: matrice.surPlaceAutoriseLivraison === true,
    carteAutoriseeRetrait: matrice.carteAutoriseeRetrait === true,
    surPlaceAutoriseRetrait: matrice.surPlaceAutoriseRetrait === true,
  };
}

/**
 * Lit prioritairement error.data.code (contrat api.js).
 */
export function getApiErrorCode(error) {
  if (!error || typeof error !== 'object') return null;
  return (
    error.data?.code
    || error.data?.error?.code
    || error.code
    || null
  );
}

export function messageForPolitiquePaiementError(error) {
  const code = getApiErrorCode(error);
  switch (code) {
    case CODE_POLITIQUE_PAIEMENT_ACTIVATION_DESACTIVEE:
      return 'L’enregistrement n’est pas encore disponible pour ce commerce.';
    case CODE_POLITIQUE_PAIEMENT_INVALIDE:
      return 'Pour chaque mode de commande actif, choisissez au moins un moyen de paiement.';
    case CODE_POLITIQUE_PAIEMENT_INACTIVE:
      return 'Enregistrez d’abord vos moyens de paiement avant de les modifier.';
    case CODE_COMMERCANT_NOT_FOUND:
      return 'Commerce introuvable.';
    default:
      return error?.message || 'Une erreur est survenue.';
  }
}

/** Défense tests : un payload ne doit jamais contenir les champs protégés pour updateCommercant. */
export function payloadContainsProtectedPolitiqueFields(payload) {
  if (!payload || typeof payload !== 'object') return false;
  return POLITIQUE_PAIEMENT_PROTECTED_FIELDS.some((k) =>
    Object.prototype.hasOwnProperty.call(payload, k)
  );
}
